/**
 * /invite/:token — accept a club invitation and set the first password.
 *
 * BR-24: a brand-new email gets this link, valid 10 days, single use.
 * Accepting signs them straight in — the API returns a full session — and
 * they land on the selector or their one club like any other sign-in.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Brand } from './SignIn';
import { FormError, Spinner } from '../components/ui';

const INPUT =
  'w-full border border-line bg-white py-3 px-4 text-[14.5px] outline-none transition-all duration-200 focus:border-gold-500 focus:ring-4 focus:ring-gold-500/20';

export function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const [invite, setInvite] = useState(null);
  const [dead, setDead] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .openInvite(token)
      .then((res) => alive && setInvite(res))
      .catch((err) => alive && setDead(err?.message ?? 'This invitation is not valid.'));
    return () => {
      alive = false;
    };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password.length < 8) {
      setError('Choose a password of at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const payload = await api.acceptInvite(token, password);
      const landing = adoptSession(payload);
      navigate(landing.kind === 'club' ? '/' : '/select-club');
    } catch (err) {
      setError(err?.message ?? 'Could not set your password. Try again.');
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-ground">
      <header className="bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)] px-5 py-4 text-white">
        <div className="mx-auto max-w-md">
          <Brand dark />
        </div>
      </header>
      <main className="mx-auto max-w-md px-5 py-10">
        {dead ? (
          <div className="border border-line bg-white p-6 text-center">
            <p className="font-market text-xl tracking-[-0.025em] font-bold text-ink-700">
              This link has expired
            </p>
            <p className="mt-2 text-sm leading-relaxed text-copy-muted">{dead}</p>
            <Link
              to="/signin"
              className="mt-4 inline-block font-market text-xs uppercase tracking-wide text-gold-800"
            >
              Go to sign in →
            </Link>
          </div>
        ) : !invite ? (
          <div className="flex justify-center py-16">
            <Spinner label="Opening your invitation" />
          </div>
        ) : (
          <form onSubmit={submit} className="border border-line bg-white p-6">
            <p className="font-market text-[11px] uppercase tracking-widest text-gold-800">
              You are invited
            </p>
            <h1 className="mt-1 font-market text-2xl tracking-[-0.03em] font-bold leading-tight text-ink-700">
              Welcome, {invite.name?.split(' ')[0]}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-copy-muted">
              {invite.clubs?.length ? (
                <>
                  You have been added to <strong>{invite.clubs.join(', ')}</strong>.
                </>
              ) : invite.portals?.length ? (
                <>
                  Your portal <strong>{invite.portals.join(', ')}</strong> is ready.
                </>
              ) : (
                <>You have been added to <strong>the club</strong>.</>
              )}{' '}
              Choose a password to activate your account — you will sign in with{' '}
              <span className="font-market">{invite.email}</span>.
            </p>

            <label className="mt-5 block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-copy-label">
                New password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={INPUT}
                autoComplete="off"
                placeholder="At least 8 characters"
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-copy-label">
                Repeat it
              </span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={INPUT}
                autoComplete="off"
              />
            </label>

            {error && <FormError>{error}</FormError>}

            <button
              type="submit"
              disabled={busy}
              className="mt-5 w-full bg-gold-500 py-3 font-market text-sm font-semibold uppercase tracking-wide text-ink-800 disabled:opacity-60"
            >
              {busy ? 'Setting up…' : 'Set password & join'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
